<template>
  <section class="relative flex flex-col h-full min-h-0 bg-white rounded-lg border border-gray-200 overflow-hidden">
    <!-- HEADER: escape hatch (#header) o título + acciones. Solo si hay algo. -->
    <header
      v-if="hasHeader"
      class="flex items-center justify-between gap-2 flex-shrink-0 px-3 py-2 text-xs font-semibold"
      :class="tone === 'primary' ? 'text-white' : 'bg-gray-50 text-gray-700 border-b border-gray-200'"
      :style="tone === 'primary' ? { backgroundColor: PRIMARY } : null"
    >
      <slot name="header">
        <span class="flex-1 truncate" style="min-width: 0" :title="title">
          <slot name="title">{{ title }}</slot>
        </span>
        <span v-if="hasSlot('actions')" class="flex items-center gap-2 flex-shrink-0">
          <slot name="actions" />
        </span>
      </slot>
    </header>

    <!-- BODY -->
    <div
      class="flex-1 min-h-0"
      :class="[bodyClass, flush ? 'p-0' : 'p-3', bodyScroll ? 'overflow-auto' : '']"
    >
      <slot />
    </div>

    <!-- FOOTER: solo si hay slot -->
    <footer
      v-if="hasSlot('footer')"
      class="flex items-center justify-end gap-2 flex-shrink-0 px-3 py-2 border-t border-gray-200 bg-gray-50"
    >
      <slot name="footer" />
    </footer>

    <!-- Overlay de carga opcional -->
    <div
      v-if="loading"
      class="absolute inset-0 flex items-center justify-center"
      style="background: rgba(255, 255, 255, 0.6); z-index: 5"
    >
      <b-icon pack="fas" icon="spinner" custom-class="fa-spin" />
    </div>
  </section>
</template>

<script>
/**
 * EpcPanel — panel genérico reutilizable: header (opcional) + body + footer (opcional).
 * Presentacional puro (sin lógica de negocio): botones y @click van en los slots.
 * Header/footer se auto-muestran según título/slots. Flex-column a altura completa:
 * header/footer fijos, solo el body scrollea.
 *
 * OJO: el Tailwind del proyecto es viejo (config `purge`, sin JIT) → NO soporta valores
 * arbitrarios (bg-[#hex], text-[11px]) ni opacidad slash (bg-white/60). Por eso los
 * colores no-estándar van por inline-style y se usan solo utilidades existentes.
 *
 * Slots: #title, #actions (derecha), default (body), #footer, #header (escape hatch).
 */
export default {
  name: 'EpcPanel',

  props: {
    title: { type: String, default: '' },
    // 'default' (claro) | 'primary' (azul EPC del diseño, texto blanco)
    tone: { type: String, default: 'default' },
    bodyScroll: { type: Boolean, default: true },
    flush: { type: Boolean, default: false },
    loading: { type: Boolean, default: false },
    bodyClass: { type: [String, Array, Object], default: '' },
  },

  data() {
    return {
      // Azul EPC del diseño (rgb(61,92,158)); inline porque el Tailwind viejo no genera bg-[#hex].
      PRIMARY: '#3d5c9e',
    }
  },

  computed: {
    hasHeader() {
      return !!this.title || this.hasSlot('title') || this.hasSlot('actions') || this.hasSlot('header')
    },
  },

  methods: {
    hasSlot(name) {
      return !!(this.$slots[name] || (this.$scopedSlots && this.$scopedSlots[name]))
    },
  },
}
</script>
